import {create} from "zustand"
import {ShadowData} from "../types/shadow"
import {getAuthHeaders, handleUnauthorized} from "../services/apiAuth"
import {READ_ONLY_REFUSED, isReadOnlyRole, onConsoleUserChange, useAuthStore} from "./authStore"

export interface DeviceShadowData {
	deviceId: string
	rawShadowData?: ShadowData
	lastUpdated: Date
	error?: string
}

export interface Device {
	device_uid: string
	user_ID: string
	[key: string]: any
}

export type ShadowType = "radar" | "classic"

export interface UpdateShadowResult {
	deviceId: string
	shadowType: ShadowType
	updatedFields: Record<string, any>
	response: any
	lastUpdated: string
}

interface IoTShadowState {
	devices: Map<string, DeviceShadowData>
	devicesList: Device[]
	isLoading: boolean
	error: string | null
	isLiveMode: boolean
	liveUpdateInterval: number
	intervalId: NodeJS.Timeout | null

	fetchDevices: (userId?: string, deviceId?: string) => Promise<Device[]>
	fetchDeviceShadow: (deviceId: string) => Promise<void>
	fetchMultipleDeviceShadows: (deviceIds: string[]) => Promise<void>
	updateDeviceShadow: (
		deviceId: string,
		shadowType: ShadowType,
		fields: Record<string, any>,
	) => Promise<UpdateShadowResult>
	startLiveUpdates: (deviceIds: string[], intervalSeconds?: number) => void
	stopLiveUpdates: () => void
	setLiveUpdateInterval: (seconds: number) => void
	clearError: () => void
	clearDevices: () => void
	clearAllAlarms: (deviceIds: string[]) => Promise<ClearAlarmsResult>
}

export interface ClearAlarmsResult {
	total: number
	succeeded: string[]
	failed: {deviceId: string; error: string}[]
}

// Clearing alarms writes to the "general" shadow (shadowType "classic" in the console).
const ALARM_SHADOW_TYPE: ShadowType = "classic"
// How long ANTITAMPER_RESET stays at 1 before it is set back to 0, so the device sees the reset.
const ANTITAMPER_RESET_HOLD_MS = 3000
const CLEAR_ALARMS_CONCURRENCY = 5

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Runs task for every item with at most `limit` tasks in flight.
const runWithConcurrency = async <T,>(
	items: T[],
	limit: number,
	task: (item: T) => Promise<void>,
): Promise<void> => {
	let next = 0
	const worker = async () => {
		while (next < items.length) {
			const item = items[next++]
			await task(item)
		}
	}
	await Promise.all(Array.from({length: Math.min(limit, items.length)}, worker))
}

// Management Lambda actions that change device state. Read-only roles (viewer / viewer_all) are
// refused here before any request is sent; the backend answers them with 403 as well.
const WRITE_ACTIONS: ReadonlySet<string> = new Set(["update-shadow"])
const isReadOnlyUser = () => isReadOnlyRole(useAuthStore.getState().userProfile?.role)

const callLambda = async (action: string, params: any = {}) => {
	if (WRITE_ACTIONS.has(action) && isReadOnlyUser()) throw new Error(READ_ONLY_REFUSED)
	try {
		const headers = getAuthHeaders()
		const response = await fetch(process.env.REACT_APP_LAMBDA_MANAGEMENT || "", {
			method: "POST",
			headers,
			body: JSON.stringify({
				action,
				...params,
			}),
		})

		if (!response.ok) {
			handleUnauthorized(response.status, headers.Authorization)
			// The management Lambda answers errors (e.g. 403 for a device the developer may not use)
			// with {error}; show that text instead of the bare status.
			let serverMessage: string | undefined
			try {
				const body = await response.json()
				serverMessage = body?.error || body?.message
			} catch {
				// not JSON
			}
			throw new Error(serverMessage || `Lambda request failed: ${response.status} ${response.statusText}`)
		}

		return await response.json()
	} catch (error) {
		console.error(`Error calling lambda with action ${action}:`, error)
		throw error
	}
}

export const useIoTShadowStore = create<IoTShadowState>((set, get) => ({
	devices: new Map(),
	devicesList: [],
	isLoading: false,
	error: null,
	isLiveMode: false,
	liveUpdateInterval: 5,
	intervalId: null,

	fetchDevices: async (userId?: string, deviceId?: string) => {
		set({isLoading: true, error: null})

		try {
			const params: any = {}
			if (userId) params.user_ID = userId
			if (deviceId) params.device_uid = deviceId

			const response = await callLambda("devices", params)
			const devices = response.devices || []

			set({
				devicesList: devices,
				isLoading: false,
			})

			return devices
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Unknown error occurred"
			set({
				error: errorMessage,
				isLoading: false,
				devicesList: [],
			})
			throw error
		}
	},

	fetchDeviceShadow: async (deviceId: string) => {
		if (!get().isLiveMode) {
			set({isLoading: true, error: null})
		}

		try {
			const response = await callLambda("shadow", {device_uid: deviceId})
			const shadowData = response.shadow

			if (shadowData) {
				const deviceData: DeviceShadowData = {
					deviceId: shadowData.deviceId,
					rawShadowData: shadowData.rawShadowData,
					lastUpdated: new Date(shadowData.lastUpdated),
					error: shadowData.error,
				}

				const nextDevices = new Map(get().devices)
				nextDevices.set(deviceId, deviceData)

				set({
					devices: nextDevices,
					isLoading: false,
				})
			}
		} catch (error) {
			console.error(`Error fetching shadow for device ${deviceId}:`, error)
			const errorMessage = error instanceof Error ? error.message : "Unknown error occurred"

			const nextDevices = new Map(get().devices)
			nextDevices.set(deviceId, {
				deviceId,
				lastUpdated: new Date(),
				error: errorMessage,
			})

			set({
				devices: nextDevices,
				error: errorMessage,
				isLoading: false,
			})
		}
	},

	fetchMultipleDeviceShadows: async (deviceIds: string[]) => {
		if (!get().isLiveMode) {
			set({isLoading: true, error: null})
		}

		try {
			const response = await callLambda("shadows", {deviceIds})
			const shadows = response.shadows || []
			const nextDevices = new Map(get().devices)

			shadows.forEach((shadowData: any) => {
				const deviceData: DeviceShadowData = {
					deviceId: shadowData.deviceId,
					rawShadowData: shadowData.rawShadowData,
					lastUpdated: new Date(shadowData.lastUpdated),
					error: shadowData.error,
				}
				nextDevices.set(shadowData.deviceId, deviceData)
			})

			set({
				devices: nextDevices,
				isLoading: false,
			})
		} catch (error) {
			console.error("Error fetching multiple device shadows:", error)
			set({
				error: error instanceof Error ? error.message : "Unknown error occurred",
				isLoading: false,
			})
		}
	},
	updateDeviceShadow: async (
		deviceId: string,
		shadowType: ShadowType,
		fields: Record<string, any>,
	) => {
		if (isReadOnlyUser()) throw new Error(READ_ONLY_REFUSED)
		set({isLoading: true, error: null})

		try {
			const response = await callLambda("update-shadow", {
				device_uid: deviceId,
				shadowType,
				fields,
			})

			const updateResult: UpdateShadowResult = response

			await get().fetchDeviceShadow(deviceId)

			set({isLoading: false})

			return updateResult
		} catch (error) {
			console.error(`Error updating shadow for device ${deviceId}:`, error)
			const errorMessage = error instanceof Error ? error.message : "Unknown error occurred"
			set({
				error: errorMessage,
				isLoading: false,
			})
			throw error
		}
	},
	startLiveUpdates: (deviceIds: string[], intervalSeconds = 5) => {
		const {stopLiveUpdates, fetchMultipleDeviceShadows} = get()

		stopLiveUpdates()
		fetchMultipleDeviceShadows(deviceIds)

		const intervalId = setInterval(() => {
			fetchMultipleDeviceShadows(deviceIds)
		}, intervalSeconds * 1000)

		set({
			isLiveMode: true,
			liveUpdateInterval: intervalSeconds,
			intervalId,
		})
	},
	stopLiveUpdates: () => {
		const {intervalId} = get()

		if (intervalId) {
			clearInterval(intervalId)
		}

		set({
			isLiveMode: false,
			intervalId: null,
		})
	},

	setLiveUpdateInterval: (seconds: number) => {
		set({liveUpdateInterval: seconds})

		const {isLiveMode} = get()
		if (isLiveMode) {
			const currentDevices = Array.from(get().devices.keys())
			get().stopLiveUpdates()
			get().startLiveUpdates(currentDevices, seconds)
		}
	},

	clearError: () => set({error: null}),

	clearAllAlarms: async (deviceIds: string[]) => {
		if (isReadOnlyUser()) throw new Error(READ_ONLY_REFUSED)
		const failed = new Map<string, string>()
		const updateAlarmFields = (deviceId: string, fields: Record<string, any>) =>
			callLambda("update-shadow", {device_uid: deviceId, shadowType: ALARM_SHADOW_TYPE, fields})

		const runStep = (ids: string[], fields: Record<string, any>, stepLabel: string, attempts = 1) =>
			runWithConcurrency(ids, CLEAR_ALARMS_CONCURRENCY, async (deviceId) => {
				for (let attempt = 1; attempt <= attempts; attempt++) {
					try {
						await updateAlarmFields(deviceId, fields)
						return
					} catch (error) {
						if (attempt < attempts) {
							await sleep(1000 * attempt)
							continue
						}
						const message = error instanceof Error ? error.message : String(error)
						failed.set(deviceId, `${stepLabel}: ${message}`)
					}
				}
			})

		const pending = () => deviceIds.filter((id) => !failed.has(id))

		// Intruder: disarm.
		await runStep(deviceIds, {ARMED: 0}, "ARMED=0 failed")
		// Antitamper: request a reset, give the devices time to see it, then clear the flag.
		await runStep(pending(), {ANTITAMPER_RESET: 1}, "ANTITAMPER_RESET=1 failed")
		await sleep(ANTITAMPER_RESET_HOLD_MS)
		// Retried, because a failure here leaves desired.ANTITAMPER_RESET stuck at 1.
		await runStep(
			pending(),
			{ANTITAMPER_RESET: 0},
			"ANTITAMPER_RESET left at 1, run Clear Alarms again",
			3,
		)

		await get().fetchMultipleDeviceShadows(deviceIds)

		return {
			total: deviceIds.length,
			succeeded: pending(),
			failed: Array.from(failed, ([deviceId, error]) => ({deviceId, error})),
		}
	},

	clearDevices: () => {
		get().stopLiveUpdates()
		set({devices: new Map(), devicesList: []})
	},
}))

// Shadows of the previous user's devices must not survive a logout / user switch.
onConsoleUserChange(() => useIoTShadowStore.getState().clearDevices())

if (typeof window !== "undefined") {
	window.addEventListener("beforeunload", () => {
		useIoTShadowStore.getState().stopLiveUpdates()
	})
}
